/**
 * Identity — admin page covering the tenant's identity
 * provider integrations plus a browser for the users / groups /
 * devices they sync. Surfaces both behind a single flat tab strip:
 *
 *   Connections · Users · Groups · Devices
 *
 * The page owns the active tab; the strip is rendered once and either
 *   - sits as the page header (Integrations tab, full-width below it)
 *   - is passed into SplitPage as `focusHeader` (browser tabs), so the
 *     left column's top tab strip aligns visually with the right
 *     column's entity tab.
 *
 * Active tab persists in `?dtab=` (default "integrations" drops out so
 * fresh URLs stay clean).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TabStrip from "../components/TabStrip.jsx";
import { ScopeLabel } from "../components/AdminViewHeader.jsx";
import IntegrationsTab from "../components/identity/IntegrationsTab.jsx";
import DirectoryBrowserTab from "../components/identity/DirectoryBrowserTab.jsx";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";

// Each tab declares the read permission its surface needs. The
// Integrations connector table reads integration configs
// (integrations.read); the Users/Groups/Devices browser reads synced
// directory data (directory_data.read). The page is reachable on EITHER
// (the nav OR-gate), so a role with just one permission must still land
// on a tab it can actually see — never on a hard-coded Integrations
// default it would 403.
export const DIRECTORY_TABS = [
    { id: "integrations", label: "Connections", permission: PERMISSIONS.INTEGRATIONS_READ },
    { id: "users",        label: "Users",        permission: PERMISSIONS.DIRECTORY_DATA_READ },
    { id: "groups",       label: "Groups",       permission: PERMISSIONS.DIRECTORY_DATA_READ },
    { id: "devices",      label: "Devices",      permission: PERMISSIONS.DIRECTORY_DATA_READ },
];

export function visibleDirectoryTabs(caps) {
    return DIRECTORY_TABS.filter((tab) => hasPermission(caps, tab.permission));
}

const BROWSER_SECTIONS = new Set(["users", "groups", "devices"]);

export default function DirectoryIntegrationsPage({ viewDef }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const caps = useMyPermissions();

    // Tabs the caller can actually open. hasPermission fails open while
    // caps load / on a pre-RBAC tenant, so the full strip shows exactly
    // as before until a concrete set says otherwise.
    const visibleTabs = useMemo(
        () => visibleDirectoryTabs(caps),
        [caps],
    );
    const visibleIds = useMemo(() => new Set(visibleTabs.map((t) => t.id)), [visibleTabs]);
    // Default to the first VISIBLE tab, not a hard-coded "integrations" —
    // an Integrations-less role lands on Users instead of a 403'd table.
    const defaultTab = visibleTabs[0]?.id ?? "integrations";

    const urlTab = searchParams.get("dtab");
    const initial = urlTab && visibleIds.has(urlTab) ? urlTab : defaultTab;
    const [active, setActive] = useState(initial);

    // If caps resolve (or change) such that the active tab is no longer
    // visible — e.g. a stale ?dtab=integrations for a directory-only role,
    // or the set arriving after first paint — snap to the first visible
    // tab so we never render a section the role can't read.
    useEffect(() => {
        if (visibleTabs.length && !visibleIds.has(active)) setActive(defaultTab);
    }, [visibleIds, active, defaultTab, visibleTabs.length]);

    useEffect(() => {
        setSearchParams((cur) => {
            const next = new URLSearchParams(cur);
            if (active && active !== "integrations") next.set("dtab", active);
            else next.delete("dtab");
            return next;
        }, { replace: true });
    }, [active, setSearchParams]);

    // Single TabStrip element reused as the page header (when on
    // Integrations) and as SplitPage's focusHeader (when on a browser
    // section). Memoized so its identity is stable across renders that
    // don't change `active` — important because SplitPage clones the
    // focusHeader element with cloneElement and re-clones every render
    // would defeat any downstream memoization.
    const tabStrip = useMemo(
        () => (
            <TabStrip
                tabs={visibleTabs.map((t) => ({
                    id: t.id,
                    label: t.label,
                    active: t.id === active,
                    dot: t.id === active ? "bg-[color:var(--green)]" : undefined,
                    onSelect: () => setActive(t.id),
                }))}
                actions={<ScopeLabel>Organization-wide</ScopeLabel>}
                className="pt-[11px] max-[900px]:pt-[8px]"
            />
        ),
        [active, visibleTabs],
    );

    if (BROWSER_SECTIONS.has(active)) {
        return (
            <DirectoryBrowserTab
                viewDef={viewDef}
                section={active}
                focusHeader={tabStrip}
                onSectionChange={setActive}
            />
        );
    }

    return (
        <>
            <header className="shrink-0 overflow-hidden">{tabStrip}</header>
            <IntegrationsTab viewDef={viewDef} />
        </>
    );
}
